/**
 * Presentation-only render quality tiers.
 * Stored as Auto / High / Medium / Low; Auto resolves from device + reducedMotion.
 */

export type RenderQualityId = "auto" | "high" | "medium" | "low";

export type ResolvedQuality = {
  id: Exclude<RenderQualityId, "auto">;
  /** Cap for renderer.setPixelRatio. */
  maxPixelRatio: number;
  antialias: boolean;
  /** Tone-mapped ACES path + composer. */
  postFx: boolean;
  bloom: boolean;
  bloomStrength: number;
  bloomRadius: number;
  bloomThreshold: number;
  antialiasPass: "smaa" | "fxaa" | "none";
  vignette: boolean;
  colorGrade: boolean;
  /** Cheap industrial PMREM for metal reflections. */
  envMap: boolean;
  envIntensity: number;
  /** Soft fill point light on the carrier pad. */
  carrierPointLight: boolean;
  textureAnisotropy: number;
  /** Combat VFX particle pool budget (active spawn cap per burst scales with this). */
  vfxBudget: number;
  speedLineCount: number;
  /** Contact-shadow softness / stretch emphasis. */
  contactShadowQuality: "full" | "simple";
};

const PRESETS: Record<Exclude<RenderQualityId, "auto">, ResolvedQuality> = {
  high: {
    id: "high",
    maxPixelRatio: 2,
    antialias: true,
    postFx: true,
    bloom: true,
    bloomStrength: 0.28,
    bloomRadius: 0.32,
    bloomThreshold: 0.84,
    antialiasPass: "smaa",
    vignette: true,
    colorGrade: true,
    envMap: true,
    envIntensity: 0.42,
    carrierPointLight: true,
    textureAnisotropy: 8,
    vfxBudget: 96,
    speedLineCount: 14,
    contactShadowQuality: "full",
  },
  medium: {
    id: "medium",
    maxPixelRatio: 1.5,
    antialias: false,
    postFx: true,
    bloom: true,
    bloomStrength: 0.2,
    bloomRadius: 0.28,
    bloomThreshold: 0.88,
    antialiasPass: "fxaa",
    vignette: false,
    colorGrade: false,
    envMap: true,
    envIntensity: 0.28,
    carrierPointLight: true,
    textureAnisotropy: 4,
    vfxBudget: 64,
    speedLineCount: 10,
    contactShadowQuality: "full",
  },
  low: {
    id: "low",
    maxPixelRatio: 1,
    antialias: false,
    postFx: false,
    bloom: false,
    bloomStrength: 0,
    bloomRadius: 0,
    bloomThreshold: 1,
    antialiasPass: "none",
    vignette: false,
    colorGrade: false,
    envMap: false,
    envIntensity: 0,
    carrierPointLight: false,
    textureAnisotropy: 1,
    vfxBudget: 32,
    speedLineCount: 6,
    contactShadowQuality: "simple",
  },
};

export const RENDER_QUALITY_IDS: RenderQualityId[] = ["auto", "high", "medium", "low"];

export function isRenderQualityId(value: unknown): value is RenderQualityId {
  return value === "auto" || value === "high" || value === "medium" || value === "low";
}

/** Prefer medium on touch / narrow viewports; low when reduced motion is on. */
export function detectAutoQuality(reducedMotion: boolean): Exclude<RenderQualityId, "auto"> {
  if (reducedMotion) return "low";
  if (typeof window === "undefined") return "high";
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  const narrow = window.matchMedia("(max-width: 1024px)").matches;
  const hoverNone = window.matchMedia("(hover: none)").matches;
  const cores = typeof navigator !== "undefined" ? (navigator.hardwareConcurrency ?? 4) : 4;
  const mem =
    typeof navigator !== "undefined"
      ? ((navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4)
      : 4;
  if (coarse || narrow || hoverNone || cores <= 4 || mem <= 2) return "medium";
  return "high";
}

export function resolveRenderQuality(
  setting: RenderQualityId,
  reducedMotion: boolean,
): ResolvedQuality {
  const tier = setting === "auto" ? detectAutoQuality(reducedMotion) : setting;
  const base = { ...PRESETS[tier] };

  // Accessibility: never run bloom / heavy grade when reduced motion is requested.
  if (reducedMotion) {
    // The post stack adds several full-screen animated passes. The direct render
    // path is calmer and substantially cheaper for this accessibility mode.
    base.postFx = false;
    base.bloom = false;
    base.bloomStrength = 0;
    base.antialiasPass = "none";
    base.vignette = false;
    base.colorGrade = false;
    base.vfxBudget = Math.min(base.vfxBudget, 16);
    base.speedLineCount = 0;
  }

  return base;
}

export function renderQualityLabel(id: RenderQualityId): string {
  switch (id) {
    case "auto":
      return "Auto";
    case "high":
      return "High";
    case "medium":
      return "Medium";
    case "low":
      return "Low";
  }
}
